import React from "react";
import { useTable, useExpanded, useSortBy, usePagination } from "react-table";
import Table from "react-bootstrap/Table";

import { Styles, PaginationText } from "./styledComponents";
import { RowProps } from "../../../types";

const getSortingProps = (disableSorting: boolean, column: any) => {
  return disableSorting
    ? null
    : { ...column.getHeaderProps(column.getSortByToggleProps()) };
};

const ReactTable = ({
  columns,
  data,
  renderRowSubComponent,
  isRowClickable = false,
  getRowProps = () => {},
  getCellProps = () => {},
  hasPagination = false,
  disableSorting = false,
  defaultSorted = [],
  tableName = "table",
  showHeader = true,
  showOuterBorder = true,
  compact = false,
  firstColumnWidth,
}: any) => {
  const options: any = {
    columns,
    data,
    autoResetSortBy: false,
    autoResetExpanded: false,
    initialState: {
      pageSize: 100,
      sortBy: defaultSorted,
    },
  };

  const {
    rows,
    page,
    prepareRow,
    headerGroups,
    getTableProps,
    getTableBodyProps,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  }: any = useTable(options, useSortBy, useExpanded, usePagination);

  const totalUsersAmount = data.length;
  const firstRowIndexOnPage = pageIndex * pageSize + 1;
  const lastRowIndexOnPage = Math.min(
    (pageIndex + 1) * pageSize,
    totalUsersAmount
  );

  return (
    <Styles
      isRowClickable={isRowClickable}
      showOuterBorder={showOuterBorder}
      compact={compact}
    >
      <Table data-test={tableName} {...getTableProps()}>
        {showHeader && (
          <thead>
            {headerGroups.map((headerGroup: any, headerGroupsIndex: number) => (
              <tr
                {...headerGroup.getHeaderGroupProps()}
                key={headerGroupsIndex}
              >
                {headerGroup.headers.map((column: any, index: number) => (
                  <th
                    data-test={column.Header}
                    key={index}
                    {...getSortingProps(disableSorting, column)}
                    style={
                      index === 0 && firstColumnWidth
                        ? {
                            width: firstColumnWidth,
                            minWidth: firstColumnWidth,
                            maxWidth: firstColumnWidth,
                            padding: 0,
                            textAlign: "center",
                          }
                        : undefined
                    }
                  >
                    {column.render("Header")}
                    <span data-test={`${column.Header}-sort`}>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? " ▼"
                          : " ▲"
                        : ""}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
        )}
        <tbody {...getTableBodyProps()}>
          {(hasPagination ? page : rows).map((row: RowProps, index: number) => {
            prepareRow(row);
            return (
              <React.Fragment key={index}>
                <tr
                  data-test={`${tableName}-row-${index}`}
                  {...row.getRowProps(getRowProps(row))}
                >
                  {row.cells.map((cell: any, cellIndex: number) => {
                    return (
                      <td
                        {...cell.getCellProps(getCellProps(cell))}
                        key={cellIndex}
                        data-test={`${tableName}-row-${index}-col${cellIndex}`}
                        style={
                          cellIndex === 0 && firstColumnWidth
                            ? {
                                width: firstColumnWidth,
                                minWidth: firstColumnWidth,
                                maxWidth: firstColumnWidth,
                                padding: 0,
                                textAlign: "center",
                              }
                            : undefined
                        }
                      >
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
                {row.isExpanded ? (
                  <tr>
                    <td colSpan={row.cells.length} style={{ padding: 0 }}>
                      {renderRowSubComponent({ row })}
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
      {hasPagination && (
        <div className="pagination">
          <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
            {"<<"}
          </button>
          <button onClick={() => previousPage()} disabled={!canPreviousPage}>
            {"<"}
          </button>
          <button onClick={() => nextPage()} disabled={!canNextPage}>
            {">"}
          </button>
          <button
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
          >
            {">>"}
          </button>
          <PaginationText>
            Showing
            <strong>
              {` ${firstRowIndexOnPage} to ${lastRowIndexOnPage} of ${totalUsersAmount} `}
            </strong>
            {`users | Go to page: `}
            <input
              type="number"
              defaultValue={pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                gotoPage(page);
              }}
              style={{ width: "100px" }}
            />
          </PaginationText>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
            }}
          >
            {[50, 75, 100].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div>
      )}
    </Styles>
  );
};

export default ReactTable;
